'use client';

import { BarChart3 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Badge } from '@/app/components/ui/badge';
import { Button } from '@/app/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/app/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/app/components/ui/tabs';

type VisitorsPeriod = 'day' | 'week' | 'month';

type PeriodStats = {
  views: number;
  unique: number;
  repeatVisits: number;
  avgPerVisitor: number;
};

type TopPage = {
  path: string;
  views: number;
};

type Props = {
  day: PeriodStats;
  week: PeriodStats;
  month: PeriodStats;
  topPages: TopPage[];
  periodIds: {
    dayId: string;
    weekId: string;
    monthId: string;
  };
};

type VisitorItem = {
  ip: string;
  visitedAt: string;
};

function StatsPeriod({
  stats,
  label,
  period,
  onOpenVisitors,
  isActive,
}: {
  stats: PeriodStats;
  label: string;
  period: VisitorsPeriod;
  onOpenVisitors: (period: VisitorsPeriod) => void;
  isActive: boolean;
}) {
  return (
    <div className="space-y-3">
      <div className="text-xs text-muted-foreground">Период: {label}</div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <Button
          variant="outline"
          onClick={() => onOpenVisitors(period)}
          className="h-auto min-h-[88px] flex-col items-start justify-center rounded-lg border p-3 text-left"
        >
          <div className="text-muted-foreground">Просмотры</div>
          <div className="text-xl font-semibold">{stats.views.toLocaleString('ru-RU')}</div>
          <div className="mt-1 text-xs text-sky-600">
            {isActive
              ? 'Список открыт ниже'
              : 'Показать живых посетителей (боты и сканеры исключаются)'}
          </div>
        </Button>
        <div className="rounded-lg border p-3">
          <div className="text-muted-foreground">Уникальные посетители</div>
          <div className="text-xl font-semibold">{stats.unique.toLocaleString('ru-RU')}</div>
        </div>
        <div className="rounded-lg border p-3">
          <div className="text-muted-foreground">Повторные визиты</div>
          <div className="text-xl font-semibold">{stats.repeatVisits.toLocaleString('ru-RU')}</div>
        </div>
        <div className="rounded-lg border p-3">
          <div className="text-muted-foreground">Среднее визитов / посетитель</div>
          <div className="text-xl font-semibold">{stats.avgPerVisitor}</div>
        </div>
      </div>
    </div>
  );
}

function periodLabel(period: VisitorsPeriod) {
  if (period === 'day') return 'сегодня';
  if (period === 'week') return 'неделю';
  return 'месяц';
}

export default function AdminTrafficStatsModal({ day, week, month, topPages, periodIds }: Props) {
  const [visitorsPeriod, setVisitorsPeriod] = useState<VisitorsPeriod | null>(null);
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<VisitorItem[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activeLabel = useMemo(() => {
    if (!visitorsPeriod) return '';
    return periodLabel(visitorsPeriod);
  }, [visitorsPeriod]);

  useEffect(() => {
    if (!visitorsPeriod) return;

    const controller = new AbortController();
    const fetchVisitors = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `/api/analytics/new-visitors?period=${visitorsPeriod}&page=${page}&pageSize=15`,
          {
            signal: controller.signal,
            cache: 'no-store',
          },
        );

        if (!response.ok) {
          throw new Error('Не удалось загрузить список посетителей.');
        }

        const payload = (await response.json()) as {
          items?: VisitorItem[];
          totalPages?: number;
          ok?: boolean;
        };

        setItems(Array.isArray(payload.items) ? payload.items : []);
        setTotalPages(Number(payload.totalPages ?? 0));
      } catch (fetchError) {
        if ((fetchError as Error).name === 'AbortError') return;
        setError('Не удалось загрузить список IP-адресов. Попробуйте позже.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchVisitors();
    return () => controller.abort();
  }, [visitorsPeriod, page]);

  const openVisitors = (period: VisitorsPeriod) => {
    setVisitorsPeriod(period);
    setPage(1);
  };
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full justify-center gap-2">
          <BarChart3 className="h-4 w-4" />
          Подробная статистика
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Аналитика посещаемости (Томск, UTC+7)</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="day" className="w-full">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="day">Сегодня</TabsTrigger>
            <TabsTrigger value="week">Неделя</TabsTrigger>
            <TabsTrigger value="month">Месяц</TabsTrigger>
          </TabsList>
          <TabsContent value="day">
            <StatsPeriod
              stats={day}
              label={periodIds.dayId}
              period="day"
              onOpenVisitors={openVisitors}
              isActive={visitorsPeriod === 'day'}
            />
          </TabsContent>
          <TabsContent value="week">
            <StatsPeriod
              stats={week}
              label={periodIds.weekId}
              period="week"
              onOpenVisitors={openVisitors}
              isActive={visitorsPeriod === 'week'}
            />
          </TabsContent>
          <TabsContent value="month">
            <StatsPeriod
              stats={month}
              label={periodIds.monthId}
              period="month"
              onOpenVisitors={openVisitors}
              isActive={visitorsPeriod === 'month'}
            />
          </TabsContent>
        </Tabs>

        {visitorsPeriod && (
          <div className="space-y-3 rounded-xl border bg-slate-50/70 p-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-slate-900">
                Уникальные браузерные посетители за {activeLabel}
              </h3>
              <Badge variant="secondary" className="text-xs">
                {items.length} на странице
              </Badge>
            </div>

            <p className="text-xs text-slate-500">
              Метрика может отличаться от Google Analytics: здесь считаются уникальные IP/браузеры с
              JS-событием, а GA применяет свои фильтры и модель сессий.
            </p>

            <div className="max-h-64 overflow-auto rounded-lg border bg-white">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-100">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-slate-700">Дата</th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-700">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td className="px-3 py-6 text-slate-500" colSpan={2}>
                        Загрузка...
                      </td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td className="px-3 py-6 text-red-500" colSpan={2}>
                        {error}
                      </td>
                    </tr>
                  ) : items.length === 0 ? (
                    <tr>
                      <td className="px-3 py-6 text-slate-500" colSpan={2}>
                        Нет данных за выбранный период.
                      </td>
                    </tr>
                  ) : (
                    items.map((item, index) => (
                      <tr key={`${item.ip}-${item.visitedAt}-${index}`} className="border-t">
                        <td className="px-3 py-2">
                          {new Date(item.visitedAt).toLocaleString('ru-RU')}
                        </td>
                        <td className="px-3 py-2 font-mono">{item.ip}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page <= 1 || isLoading}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              >
                Назад
              </Button>
              <span className="text-xs text-slate-500">
                Страница {page} из {Math.max(totalPages, 1)}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isLoading || page >= Math.max(totalPages, 1)}
                onClick={() => setPage((prev) => prev + 1)}
              >
                Вперед
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <div className="text-sm font-medium">Топ страниц за сегодня</div>
          <div className="flex flex-wrap gap-2">
            {topPages.length === 0 ? (
              <Badge variant="secondary">Пока нет данных</Badge>
            ) : (
              topPages.map((item) => (
                <Badge key={item.path} variant="outline" className="text-xs">
                  {item.path} · {item.views}
                </Badge>
              ))
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
